import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { BiPlus } from 'react-icons/bi';
import useQuotes from '../hooks/useQuotes';
import DataTable from '../components/utility/dataTable';
import CreateQuoteModal from '../components/quote/createQuoteModal';
import { formatDate, formatInstant, formatCurrency } from '../utils/formatters';
import { QUOTE_STATUS_FILTER_OPTIONS, STATUS_COLORS } from '../utils/quoteStatuses';

// `key` is the JSON property name returned by the API (used for cell render
// and default backend sort/search). `path` is the dotted property path the
// backend resolves via reflection — needed when the column reads through a
// navigation property (e.g. `customer.name` off the Customer nav).
//
// DataTable hands renderers (value, row). Quotes inherit currency from their
// customer — the server projects CustomerCurrency onto each row so we can
// format the Total in the right symbol without a second fetch.
const money = (v, row) => formatCurrency(v, row?.customerCurrency);
const statusChip = (v) => {
    const sc = STATUS_COLORS[v] || STATUS_COLORS.Draft;
    return (
        <span style={{ backgroundColor: sc.bg, color: sc.text, padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {v}
        </span>
    );
};
const ALL_COLUMNS = [
    { key: 'quoteNumber',  label: 'Quote #',  system: true },
    { key: 'title',        label: 'Title',    system: false },
    { key: 'customerName', path: 'customer.name', label: 'Customer', system: false },
    { key: 'status',       label: 'Status',   system: false, render: statusChip },
    { key: 'total',        label: 'Total',    system: false, render: money },
    { key: 'quotedAt',     label: 'Quoted On',  system: false, render: formatDate },
    { key: 'expiryDate',   label: 'Expires On', system: false, render: formatDate },
    { key: 'createdAt',    label: 'Created On', system: false, render: formatInstant },
];

const DEFAULT_VISIBLE = {
    quoteNumber: true,
    title: true,
    customerName: true,
    status: true,
    total: true,
    quotedAt: true,
    expiryDate: false,
    createdAt: false,
};

const TABLE_NAME = 'marginpal-quotes';

function loadVisibleColumns() {
    try {
        const stored = localStorage.getItem(TABLE_NAME);
        // Merge so newly-added columns pick up their default visibility
        // instead of silently being undefined (= hidden) for existing users.
        if (stored) return { ...DEFAULT_VISIBLE, ...JSON.parse(stored) };
    } catch { /* fall through to default */ }
    return DEFAULT_VISIBLE;
}

// Default set: every non-system column that's visible by default. Mirrors
// the derivation inside DataTable so "Reset to Defaults" and the initial
// filter seed agree on the same set.
const DEFAULT_SEARCH_FIELDS = ALL_COLUMNS
    .filter(col => !col.system && DEFAULT_VISIBLE[col.key])
    .map(col => col.path ?? col.key);

function loadSearchableFields() {
    try {
        const stored = localStorage.getItem(`${TABLE_NAME}_search`);
        if (stored) return JSON.parse(stored);
    } catch { /* fall through to default */ }
    return DEFAULT_SEARCH_FIELDS;
}

const DEFAULT_FILTER = {
    pageNumber: 1,
    pageSize: 20,
    sortOptions: { key: 'createdAt', direction: 'descending' },
    // Seeded in useState initializer below so the first query already has
    // the persisted searchFields — previously DataTable pushed them up on
    // mount, which fired a wasted initial request with empty fields.
    searchOptions: { searchTerm: '', searchFields: [] },
    statusFilter: null,
};

export default function QuotesPage() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState(() => ({
        ...DEFAULT_FILTER,
        searchOptions: { searchTerm: '', searchFields: loadSearchableFields() },
    }));
    const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
    const [addOpen, setAddOpen] = useState(false);

    const { data, isLoading, error } = useQuotes(filter);

    const columns = ALL_COLUMNS.filter(col => visibleColumns[col.key]);

    const filters = [
        {
            id: 'status',
            type: 'select',
            label: 'Status',
            placeholder: 'All statuses',
            options: QUOTE_STATUS_FILTER_OPTIONS,
            value: filter.statusFilter,
            isClearable: true,
            // Inline beside the search box, not in the advanced drawer.
            advanced: false,
        },
    ];

    const handleFilterChange = ({ filterId, value }) => {
        setFilter(prev => {
            switch (filterId) {
                case 'pageNumber':
                    return { ...prev, pageNumber: Number(value) };
                case 'pageSize':
                    return { ...prev, pageSize: Number(value), pageNumber: 1 };
                case 'sortOptions':
                    return { ...prev, sortOptions: value, pageNumber: 1 };
                case 'search':
                    return {
                        ...prev,
                        pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchTerm: value },
                    };
                case 'searchFields':
                    return {
                        ...prev,
                        pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchFields: value },
                    };
                case 'status':
                    return { ...prev, statusFilter: value, pageNumber: 1 };
                default:
                    return prev;
            }
        });
    };

    return (
        <div>
            <h1 className="fw-bold mb-1">Quotes</h1>
            <p className="text-muted mb-4">Create and manage your quotes.</p>
            <DataTable
                tableName={TABLE_NAME}
                columns={columns}
                data={data?.items ?? []}
                isLoading={isLoading}
                error={error}
                totalRecords={data?.totalRecords ?? 0}
                filter={filter}
                filters={filters}
                onRowClick={(e, row) => navigate(`/quotes/${row.id}`)}
                actions={[
                    <Button color="primary" size="sm" onClick={() => setAddOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiPlus /> Add Quote
                    </Button>
                ]}
                showSettings
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE}
                settingsColumns={ALL_COLUMNS}
                onFilterChange={handleFilterChange}
                sortConfiguration={{ key: 'createdAt', direction: 'descending' }}
            />
            <CreateQuoteModal isOpen={addOpen} onClose={() => setAddOpen(false)} />
        </div>
    );
}
